// EsignDialog.tsx — ЕДИНЫЙ диалог подписи квалифицированной ЭЦП.
//
// Все сценарии подписи в кабинете идут через него: документы заявки, пользовательское соглашение,
// заявление на отзыв заявки. Раньше на каждый сценарий была своя обёртка с одинаковым состоянием,
// одинаковым запросом одноразового кода и одинаковой логикой закрытия — различались только
// вызываемый эндпоинт и тексты. Копии успели разойтись по поведению (правило закрытия по клику
// мимо окна пришлось чинить в каждой отдельно), поэтому обобщение — не косметика.
//
// Здесь живёт всё, что одинаково:
//   * состояние формы (ИНН, режим юрлица, ИНН компании, признак «код отправлен», код);
//   * сброс состояния при открытии;
//   * запрос одноразового кода через GET AppFile/SendCode;
//   * индикатор загрузки, разбор отказа, снекбары;
//   * правило закрытия: только явным действием (крестик или «Отмена»), но НЕ кликом мимо окна и
//     не Escape — промах мышью посреди подписи сбрасывал ИНН и уже отправленный одноразовый код.
//
// Сценарий-специфичное вызывающий передаёт пропами: сам запрос подписи, признак успеха, что
// делать после успеха и какие отказы считать окончательными.
import { useEffect, useState } from "react";
import styled from "styled-components";
import Dialog from "@mui/material/Dialog";
import MuiDialogContent from "@mui/material/DialogContent";
import { useTranslation } from "react-i18next";
import MainStore from "./../MainStore";
import EsignPinForm from "./EsignPinForm";
import DialogCloseButton from "./DialogCloseButton";
import { sendCode } from "api/FileSign";
import { getSignFailure, isSignSuccess, SignFailure } from "api/FileSign/signError";

/** Реквизиты подписанта, введённые в форме. */
export interface EsignCredentials {
  pin: string;
  code: string;
  /** ИНН организации — только когда подпись идёт от имени юрлица. */
  pinOrganization?: string;
}

export interface EsignDialogProps {
  open: boolean;
  /** id окна в DOM: у каждого сценария свой, чтобы e2e и тесты их различали. */
  id: string;
  title: string;

  /** ИНН подписанта-физлица, подставляемый в поле при открытии. */
  defaultPin?: string;
  /**
   * ИНН организации. Включает режим «юрлицо»; поле подписанта при этом остаётся пустым — ЭЦП
   * выдаётся человеку, и туда вводится ЛИЧНЫЙ ИНН. ИНН компании сюда подставлять нельзя: боевой
   * CDS вернёт personIdnp директора, и серверная сверка отклонит подпись (CDS_PIN_MISMATCH).
   */
  defaultCompanyPin?: string;
  cancelLabel?: string;

  /** Запрос подписи — единственное, что действительно различается между сценариями. */
  sign: (credentials: EsignCredentials) => Promise<any>;
  /**
   * Признак успеха. По умолчанию — HTTP 200/201. Переопределяется там, где успех определяется
   * телом ответа (акт соглашения: без acceptance_id подписи не было).
   */
  isSigned?: (response: any) => boolean;
  /** Текст снекбара при успехе. */
  successMessage: string;
  /** Что делать после успеха. Диалог к этому моменту уже сброшен. */
  onSigned: (response: any, pin: string) => void;

  /**
   * Отказ, после которого повторять ввод кода бессмысленно (заявку уже приняли в работу, чужая
   * заявка). По умолчанию таких нет: любой отказ оставляет диалог открытым, чтобы пользователь
   * ввёл код заново. Причину показывает interceptor — второй раз про неё не сообщаем.
   */
  isTerminalFailure?: (failure: SignFailure) => boolean;
  /** Вызывается вместо onSigned, когда отказ признан окончательным. */
  onTerminalFailure?: (failure: SignFailure) => void;

  onClose: () => void;
}

const EsignDialog = (props: EsignDialogProps) => {
  const { t } = useTranslation();

  const [pin, setPin] = useState("");
  const [code, setCode] = useState("");
  const [isSend, setIsSend] = useState(false);
  const [isLegal, setIsLegal] = useState(false);
  const [companyPin, setCompanyPin] = useState("");

  const resetForm = () => {
    const isCompany = Boolean(props.defaultCompanyPin);
    setPin(isCompany ? "" : props.defaultPin || "");
    setCode("");
    setIsSend(false);
    setIsLegal(isCompany);
    setCompanyPin(props.defaultCompanyPin || "");
  };

  useEffect(() => {
    if (props.open) {
      resetForm();
    }
    // resetForm пересоздаётся каждый рендер; зависимости — только то, от чего зависит результат.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.defaultPin, props.defaultCompanyPin]);

  const organizationPin = isLegal ? companyPin : undefined;

  const handleSendCode = async () => {
    setIsSend(true);

    try {
      MainStore.changeLoader(true);
      const response = await sendCode(pin, organizationPin);

      if (isSignSuccess(response)) {
        MainStore.setSnackbar(t("label:digitalSign.pinCodeSent"), "success");
        return;
      }

      // Отказ ОЭП с причиной (нет сертификата, учётка заблокирована) уже показан interceptor'ом.
      if (getSignFailure(response)) {
        return;
      }

      throw new Error();
    } catch (err) {
      // Обёртки http.* возвращают AxiosError объектом, но interceptor отклоняет промис на 4xx —
      // отказ с {message, code} может прийти и сюда.
      if (getSignFailure(err)) {
        return;
      }
      MainStore.setSnackbar(t("label:digitalSign.noDigitalSignature"), "error");
    } finally {
      MainStore.changeLoader(false);
    }
  };

  const handleSign = async () => {
    try {
      MainStore.changeLoader(true);
      const response = await props.sign({ pin, code, pinOrganization: organizationPin });

      const signed = props.isSigned ? props.isSigned(response) : isSignSuccess(response);
      if (signed) {
        MainStore.setSnackbar(props.successMessage, "success");
        setIsSend(false);
        props.onSigned(response, pin);
        return;
      }

      const failure = getSignFailure(response);
      if (failure) {
        if (props.isTerminalFailure?.(failure)) {
          props.onTerminalFailure?.(failure);
        }
        // Иначе диалог остаётся открытым: причину показал interceptor, код можно ввести заново.
        return;
      }

      throw new Error();
    } catch (err) {
      const failure = getSignFailure(err);
      if (failure) {
        if (props.isTerminalFailure?.(failure)) {
          props.onTerminalFailure?.(failure);
        }
        return;
      }
      MainStore.setSnackbar(t("message:somethingWentWrong"), "error");
    } finally {
      MainStore.changeLoader(false);
    }
  };

  const handleClose = () => {
    resetForm();
    props.onClose();
  };

  return (
    <Dialog
      // onClose намеренно не задан, Escape отключён: закрытие только явным действием.
      aria-labelledby={`${props.id}-title`}
      fullWidth={true}
      id={props.id}
      maxWidth={"xs"}
      open={props.open}
      disableEscapeKeyDown
    >
      <DialogCloseButton id={`${props.id}CloseButton`} onClick={handleClose} />
      <ContentWrapper>
        <EsignPinForm
          title={props.title}
          pin={pin}
          onPinChange={setPin}
          isLegal={isLegal}
          onIsLegalChange={setIsLegal}
          companyPin={companyPin}
          onCompanyPinChange={setCompanyPin}
          isSend={isSend}
          code={code}
          onCodeChange={setCode}
          onSendCode={handleSendCode}
          onSign={handleSign}
          onCancel={handleClose}
          cancelLabel={props.cancelLabel}
        />
      </ContentWrapper>
    </Dialog>
  );
};

export default EsignDialog;

const ContentWrapper = styled(MuiDialogContent)`
  margin: 20px;
`;
